import React, { useContext, useEffect, useRef, useState } from "react";

import { format, isSameDay, parseISO } from "date-fns";
import { useHistory, useParams } from "react-router-dom";
import Avatar from "@material-ui/core/Avatar";
import { makeStyles } from "@material-ui/core/styles";
import { Chip, IconButton, Tooltip } from "@material-ui/core";
import { i18n } from "../../translate/i18n";

import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";

import { v4 as uuidv4 } from "uuid";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";

import TicketMessagesDialog from "../TicketMessagesDialog";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import TakeOverTicketModal from "../TakeOverTicketModal";
import { getInitials } from "../../helpers/getInitials";
import { generateColor } from "../../helpers/colorGenerator";

const hexToRgba = (hex, alpha = 0.15) => {
  if (!hex || typeof hex !== "string") return `rgba(124, 124, 124, ${alpha})`;
  let c = hex.replace("#", "").trim();
  if (c.length === 3) {
    c = c.split("").map(char => char + char).join("");
  }
  if (c.length === 6) {
    const num = parseInt(c, 16);
    if (!isNaN(num)) {
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  return hex;
};

const useStyles = makeStyles((theme) => ({
  ticketItem: {
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    padding: "10px 12px 10px 16px",
    cursor: "pointer",
    gap: 10,
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    [theme.breakpoints.down("sm")]: {
      padding: "8px 8px 8px 12px",
      gap: 8,
    },
  },
  ticketItemSelected: {
    backgroundColor: theme?.mode === "light"
      ? "rgba(255,53,0,0.06) !important"
      : "rgba(255,53,0,0.12) !important",
    borderRight: "3px solid rgba(255,53,0,0.8)",
  },
  ticketItemPending: {
    cursor: "default",
  },
  queueBar: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderRadius: "0 2px 2px 0",
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: 2,
    [theme.breakpoints.down("sm")]: {
      width: 40,
      height: 40,
    },
  },
  contentArea: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  topLine: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
  },
  contactName: {
    fontSize: 15,
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    minWidth: 0,
    lineHeight: 1.3,
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
    },
  },
  timeText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  timeTextUnread: {
    color: "rgba(255,53,0,0.8)",
    fontWeight: 600,
  },
  messageLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 2,
  },
  lastMessage: {
    fontSize: 13.5,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    lineHeight: 1.4,
  },
  draftLabel: {
    color: "#00a884",
    fontWeight: 500,
    fontSize: 13.5,
  },
  draftMessage: {
    fontSize: 13.5,
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    lineHeight: 1.4,
  },
  unreadBadge: {
    backgroundColor: "rgba(255,53,0,0.8)",
    color: "#fff",
    borderRadius: 12,
    minWidth: 20,
    height: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 700,
    padding: "0 5px",
    flexShrink: 0,
  },
  metaLine: {
    display: "flex",
    gap: 4,
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 4,
    marginBottom: 2,
  },
  badgePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    height: 19,
    padding: "0 7px",
    borderRadius: 6,
    fontSize: "0.62rem",
    fontWeight: 600,
    letterSpacing: "0.2px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 160,
    lineHeight: 1,
    boxSizing: "border-box",
    userSelect: "none",
    transition: "all 0.15s ease",
  },
  badgeDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    flexShrink: 0,
  },
  tagBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    height: 19,
    padding: "0 6px",
    borderRadius: 6,
    fontSize: "0.62rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 150,
    lineHeight: 1,
    boxSizing: "border-box",
    cursor: "default",
    userSelect: "none",
  },
  tagMoreBadge: {
    height: 19,
    padding: "0 5px",
    borderRadius: 6,
    fontSize: "0.6rem",
    fontWeight: 700,
    backgroundColor: theme?.mode === "light" ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.08)",
    color: theme?.mode === "light" ? "#555" : "#aaa",
    border: `1px solid ${theme?.mode === "light" ? "rgba(0,0,0,0.12)" : "rgba(255,255,255,0.15)"}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    cursor: "pointer",
  },
  aiChip: {
    height: 19,
    padding: "0 6px",
    fontSize: "0.62rem",
    fontWeight: 700,
    borderRadius: 6,
    backgroundColor: "rgba(156, 39, 176, 0.15)",
    color: "#ba68c8",
    border: "1px solid rgba(156, 39, 176, 0.4)",
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 1,
  },
  actionLine: {
    display: "flex",
    gap: 6,
    marginTop: 5,
    flexWrap: "wrap",
  },
  previewButton: {
    color: theme.palette.text.secondary,
    padding: 4,
    marginRight: 2,
  },
  actionBtn: {
    fontSize: "0.7rem",
    padding: "3px 12px",
    borderRadius: 4,
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
    color: "#fff",
    minWidth: 0,
    textTransform: "none",
    lineHeight: 1.5,
    boxShadow: "none",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.65rem",
      padding: "2px 8px",
    },
  },
  itemDivider: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginLeft: 74,
    [theme.breakpoints.down("sm")]: {
      marginLeft: 60,
    },
  },
  presence: {
    color: theme?.mode === "light" ? "blue" : "lightgreen",
    fontWeight: "bold",
    fontSize: 13,
  },
}));
  {/*PLW DESIGN INSERIDO O dentro do const handleChangeTab*/}
  const TicketListItemCustom = ({ ticket }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [ticketUser, setTicketUser] = useState(null);
  const [ticketQueueName, setTicketQueueName] = useState(null);
  const [ticketQueueColor, setTicketQueueColor] = useState(null);
  const [ticketQueueOptionName, setTicketQueueOptionName] = useState(null);
  const [tag, setTag] = useState([]);
  const [whatsAppName, setWhatsAppName] = useState(null);
  const [lastInteractionLabel, setLastInteractionLabel] = useState('');
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const [verpreview, setverpreview] = useState(false);
  const { profile } = user;
  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [takeOverTicketModalOpen, setTakeOverTicketModalOpen] = useState(false);
  const presenceMessage = { composing: "Digitando...", recording: "Gravando..." };
  const [draftText, setDraftText] = useState("");

  useEffect(() => {
    const loadDraft = () => {
      const saved = localStorage.getItem(`draft_${ticket.id}`);
      setDraftText(saved || "");
    };
    loadDraft();
    window.addEventListener('draftUpdate', loadDraft);
    return () => window.removeEventListener('draftUpdate', loadDraft);
  }, [ticket.id]);
  
  useEffect(() => {
    if (ticket.userId && ticket.user) {
      setTicketUser(ticket.user?.name?.toUpperCase());
    } else if (!ticket.userId) {
      setTicketUser(null);
    }
    setTicketQueueName(ticket.queue?.name?.toUpperCase() || null);
    setTicketQueueColor(ticket.queue?.color || null);
    setTicketQueueOptionName(ticket.queueOption?.title?.toUpperCase() || null);

    if (ticket.whatsappId && ticket.whatsapp) {
      setWhatsAppName(ticket.whatsapp.name?.toUpperCase());
    }

    setTag(ticket?.tags || []);

    return () => {
      isMounted.current = false;
    };
  }, [ticket]);

  {/*CÓDIGO NOVO SAUDAÇÃO*/}
  const handleCloseTicket = async (id, justClose = false) => {
    setTag(ticket?.tags);
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
		justClose: justClose,
        userId: user?.id,
        queueId: ticket?.queue?.id,
        useIntegration: false,
        promptId: null,
        integrationId: null
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }
    history.push(`/tickets/`);
  };

  useEffect(() => {
    const renderLastInteractionLabel = () => {
      let labelColor = '';
      let labelText = '';

      if (!ticket.lastMessage) return '';

      const lastInteractionDate = parseISO(ticket.updatedAt);
      const currentDate = new Date();
      const timeDifference = currentDate - lastInteractionDate;
      const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
      const minutesDifference = Math.floor(timeDifference / (1000 * 60));


      if (minutesDifference >= 3 && minutesDifference <= 10) {
        labelText = `(${minutesDifference} m atrás)`;
        labelColor = 'green';
      } else if (minutesDifference >= 30 && minutesDifference < 60) {
        labelText = `(${minutesDifference} m atrás)`;
        labelColor = 'Orange';
      } else if (minutesDifference > 60  && hoursDifference < 24) {
        labelText = `(${hoursDifference} h atrás)`;
        labelColor = 'red';
      } else if (hoursDifference >= 24) {
        labelText = `(${Math.floor(hoursDifference / 24)} dias atrás)`;
        labelColor = 'red';
      }


      return { labelText, labelColor };
    };

    // Função para atualizar o estado do componente
    const updateLastInteractionLabel = () => {
      const { labelText, labelColor } = renderLastInteractionLabel();
      setLastInteractionLabel(
        <span style={{ color: labelColor, fontSize: 11, fontWeight: 600 }}>
          {labelText}
        </span>
      );
      // Agendando a próxima atualização após 30 segundos
      setTimeout(updateLastInteractionLabel, 30 * 1000);
    };

    // Inicializando a primeira atualização
    updateLastInteractionLabel();

  }, [ticket]); // Executando apenas uma vez ao montar o componente

  const handleReopenTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
        queueId: ticket?.queue?.id
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }
    history.push(`/tickets/${ticket.uuid}`);
  };

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${id}`, {
                status: "open",
                userId: user?.id,
            });
            
            let settingIndex;

            try {
                const { data } = await api.get("/settings/");
                
                settingIndex = data.filter((s) => s.key === "sendGreetingAccepted");
                
            } catch (err) {
                toastError(err);
                   
            }
            
            if (settingIndex[0].value === "enabled" && !ticket.isGroup) {
                handleSendMessage(ticket.id);
                
            }

        } catch (err) {
            setLoading(false);
            
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }

        // handleChangeTab(null, "tickets");
        // handleChangeTab(null, "open");
        history.push(`/tickets/${ticket.uuid}`);
    };
	
	    const handleSendMessage = async (id) => {
        
        const msg = `{{ms}} *{{name}}*, meu nome é *${user?.name}* e agora vou prosseguir com seu atendimento!`;
        const message = {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `*Mensagem Automática:*\n${msg.trim()}`,
        };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) {
            toastError(err);
            
        }
    };
	{/*CÓDIGO NOVO SAUDAÇÃO*/}

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
    history.push(`/tickets/${uuid}`);
  };




  const handleOpenTransferModal = () => {
    setTransferTicketModalOpen(true);
  }

  const handleCloseTransferTicketModal = () => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
    }
  };

  return (
    <React.Fragment key={ticket.id}>
      <TransferTicketModalCustom
        modalOpen={transferTicketModalOpen}
        onClose={handleCloseTransferTicketModal}
        ticketid={ticket.id}
      />
      <TakeOverTicketModal
        modalOpen={takeOverTicketModalOpen}
        onClose={() => setTakeOverTicketModalOpen(false)}
        ticket={ticket}
      />
      <TicketMessagesDialog
        open={openTicketMessageDialog}
        handleClose={() => setOpenTicketMessageDialog(false)}
        ticketId={ticket.id}
      />

      <div
        className={`${classes.ticketItem} ${
          ticketId && ticketId === ticket.uuid ? classes.ticketItemSelected : ""
        } ${ticket.status === "pending" ? classes.ticketItemPending : ""}`}
        onClick={() => {
          if (ticket.status === "pending") return;
          handleSelectTicket(ticket);
        }}
      >
        {/* Queue color bar */}
        <Tooltip arrow placement="right" title={ticket.queue?.name?.toUpperCase() || "SEM FILA"}>
          <span
            className={classes.queueBar}
            style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
          />
        </Tooltip>

        {/* Avatar */}
        <Avatar
          className={classes.avatar}
          src={ticket?.contact?.profilePicUrl}
          style={{ backgroundColor: generateColor(ticket?.contact?.number) }}
        >
          {getInitials(ticket?.contact?.name || "")}
        </Avatar>

        {/* Content */}
        <div className={classes.contentArea}>
          {/* Line 1: Name + Time */}
          <div className={classes.topLine}>
            <span className={classes.contactName}>{ticket.contact.name}</span>
            {ticket.lastMessage && (
              <span
                className={`${classes.timeText} ${
                  ticket.unreadMessages > 0 ? classes.timeTextUnread : ""
                }`}
              >
                {isSameDay(parseISO(ticket.updatedAt), new Date())
                  ? format(parseISO(ticket.updatedAt), "HH:mm")
                  : format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}
              </span>
            )}
          </div>

          {/* Line 2: Message preview / Draft + Unread badge */}
          <div className={classes.messageLine}>
            {draftText ? (
              <span className={classes.draftMessage}>
                <span className={classes.draftLabel}>Rascunho: </span>
                {draftText}
              </span>
            ) : (
              <span className={classes.lastMessage}>
                {["composing", "recording"].includes(ticket?.presence) ? (
                  <span className={classes.presence}>
                    {presenceMessage[ticket.presence]}
                  </span>
                ) : ticket.lastMessage ? (
                  ticket.lastMessage.includes("data:image/png;base64")
                    ? "📍 Localização"
                    : (ticket.lastMessage.trim().toLowerCase() === "sticker" || ticket.lastMessage.trim().toLowerCase() === "figurinha")
                    ? "👾 Figurinha"
                    : ticket.lastMessage
                ) : (
                  ""
                )}
              </span>
            )}
            {ticket.unreadMessages > 0 && (
              <span className={classes.unreadBadge}>{ticket.unreadMessages}</span>
            )}
          </div>

          {/* Line 3: Meta badges */}
          <div className={classes.metaLine}>
            {/* Conexão WhatsApp */}
            {ticket?.whatsapp?.name && (
              <div
                className={classes.badgePill}
                style={{
                  backgroundColor: "rgba(46, 125, 50, 0.12)",
                  color: "#4caf50",
                  border: "1px solid rgba(46, 125, 50, 0.35)",
                }}
                title={`Conexão: ${ticket.whatsapp.name}`}
              >
                <span
                  className={classes.badgeDot}
                  style={{ backgroundColor: "#4caf50" }}
                />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {ticket.whatsapp.name.toUpperCase()}
                </span>
              </div>
            )}

            {/* Fila / Subfila (exibe somente a última escolha) */}
            {(() => {
              const queueTitle = (ticket.queueOption?.title || ticketQueueOptionName || ticket.queue?.name || ticketQueueName || "SEM FILA")?.toUpperCase();
              const qColor = ticket.queue?.color || ticketQueueColor || "#7c7c7c";
              return (
                <div
                  className={classes.badgePill}
                  style={{
                    backgroundColor: hexToRgba(qColor, 0.14),
                    color: qColor,
                    border: `1px solid ${hexToRgba(qColor, 0.45)}`,
                  }}
                  title={ticket.queueOption?.title ? `Subfila: ${queueTitle} (Fila: ${ticket.queue?.name || ""})` : `Fila: ${queueTitle}`}
                >
                  <span
                    className={classes.badgeDot}
                    style={{ backgroundColor: qColor }}
                  />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {queueTitle}
                  </span>
                </div>
              );
            })()}

            {/* Tag do Contato / Ticket (identificação de onde o contato é) */}
            {(() => {
              const allTags = (ticket.tags && ticket.tags.length > 0)
                ? ticket.tags
                : (ticket.contact?.tags && ticket.contact.tags.length > 0)
                ? ticket.contact.tags
                : (tag && tag.length > 0)
                ? tag
                : [];

              if (!allTags || allTags.length === 0) return null;
              const primaryTag = allTags[0];
              const tColor = primaryTag.color || "#0288d1";
              const extraCount = allTags.length - 1;

              return (
                <React.Fragment key={primaryTag.id || "primary-tag"}>
                  <Tooltip
                    title={`Tag: ${primaryTag.name}`}
                    arrow
                    placement="top"
                  >
                    <div
                      className={classes.tagBadge}
                      style={{
                        backgroundColor: hexToRgba(tColor, 0.16),
                        color: tColor,
                        border: `1px solid ${hexToRgba(tColor, 0.45)}`,
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="10"
                        height="10"
                        fill="currentColor"
                        style={{ flexShrink: 0, marginRight: 2 }}
                      >
                        <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 6.33 7 5.5 7z" />
                      </svg>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {primaryTag.name.toUpperCase()}
                      </span>
                    </div>
                  </Tooltip>
                  {extraCount > 0 && (
                    <Tooltip
                      title={allTags.slice(1).map(t => t.name).join(", ")}
                      arrow
                      placement="top"
                    >
                      <div className={classes.tagMoreBadge}>
                        +{extraCount}
                      </div>
                    </Tooltip>
                  )}
                </React.Fragment>
              );
            })()}

            {/* Atendente Atribuído */}
            {ticketUser && (
              <div
                className={classes.badgePill}
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.06)",
                  color: "#b0bec5",
                  border: "1px solid rgba(255, 255, 255, 0.14)",
                }}
                title={`Atribuído a: ${ticketUser}`}
              >
                <span style={{ fontSize: "0.65rem", opacity: 0.8 }}>👤</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  {ticketUser}
                </span>
              </div>
            )}

            {/* Atendimento IA */}
            {ticket.useIntegration && ticket.promptId && !ticket.userId && (
              <div
                className={classes.aiChip}
                title="Atendimento por Inteligência Artificial"
              >
                🤖 IA
              </div>
            )}
          </div>

          {/* Line 4: Action buttons */}
          {!ticket.contact?.isNewsletter && (ticket.status === "pending" || ticket.status === "attending") && (
            <div className={classes.actionLine}>
              <Tooltip arrow placement="top" title="Visualizar sem aceitar">
                <IconButton
                  size="small"
                  className={classes.previewButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenTicketMessageDialog(true);
                  }}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow placement="top" title="Aceitar a conversa">
                <ButtonWithSpinner
                  className={classes.actionBtn}
                  style={{ backgroundColor: "#2e7d32" }}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => { e.stopPropagation(); handleAcepptTicket(ticket.id); }}
                >
                  {i18n.t("ticketsList.buttons.accept")}
                </ButtonWithSpinner>
              </Tooltip>
              <Tooltip arrow placement="top" title="Finalizar a conversa e enviar mensagem de conclusão">
                <ButtonWithSpinner
                  className={classes.actionBtn}
                  style={{ backgroundColor: "#c62828" }}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id, false); }}
                >
                  {i18n.t("ticketsList.buttons.closed")}
                </ButtonWithSpinner>
              </Tooltip>
              <Tooltip arrow placement="top" title="Fechar a conversa sem enviar mensagem de conclusão">
                <ButtonWithSpinner
                  className={classes.actionBtn}
                  style={{ backgroundColor: "#8B0000" }}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id, true); }}
                >
                  Fechar
                </ButtonWithSpinner>
              </Tooltip>
            </div>
          )}
          {!ticket.contact?.isNewsletter && ticket.status !== "closed" && ticket.status !== "pending" && ticket.status !== "attending" && (
            <div className={classes.actionLine}>
              {ticket.userId && Number(ticket.userId) !== Number(user?.id) && (
                <Tooltip arrow placement="top" title="Assumir este atendimento e dar continuidade">
                  <ButtonWithSpinner
                    className={classes.actionBtn}
                    style={{ backgroundColor: "#2e7d32" }}
                    variant="contained"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTakeOverTicketModalOpen(true);
                    }}
                  >
                    Assumir
                  </ButtonWithSpinner>
                </Tooltip>
              )}
              <Tooltip arrow placement="top" title="Transferir a conversa">
                <ButtonWithSpinner
                  className={classes.actionBtn}
                  style={{ backgroundColor: "#1565c0" }}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => { e.stopPropagation(); handleOpenTransferModal(); }}
                >
                  {i18n.t("ticketsList.buttons.transfer")}
                </ButtonWithSpinner>
              </Tooltip>
              <Tooltip arrow placement="top" title="Finalizar a conversa e enviar mensagem de conclusão">
                <ButtonWithSpinner
                  className={classes.actionBtn}
                  style={{ backgroundColor: "#c62828" }}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id, false); }}
                >
                  {i18n.t("ticketsList.buttons.closed")}
                </ButtonWithSpinner>
              </Tooltip>
              <Tooltip arrow placement="top" title="Fechar a conversa sem enviar mensagem de conclusão">
                <ButtonWithSpinner
                  className={classes.actionBtn}
                  style={{ backgroundColor: "#8B0000" }}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id, true); }}
                >
                  Fechar
                </ButtonWithSpinner>
              </Tooltip>
            </div>
          )}
          {!ticket.contact?.isNewsletter && ticket.status === "closed" && (
            <div className={classes.actionLine}>
              <Tooltip arrow placement="top" title="Reabrir a conversa">
                <ButtonWithSpinner
                  className={classes.actionBtn}
                  style={{ backgroundColor: "#c62828" }}
                  variant="contained"
                  size="small"
                  loading={loading}
                  onClick={(e) => { e.stopPropagation(); handleReopenTicket(ticket.id); }}
                >
                  {i18n.t("ticketsList.buttons.reopen")}
                </ButtonWithSpinner>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      <div className={classes.itemDivider} />
    </React.Fragment>
  );
};

export default TicketListItemCustom;
